import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';

// === 前台頁面 ===
import HomePage from './HomePage';
import RestaurantBooking from './RestaurantBooking';
import LoginPage from './LoginPage';
import OrderHistoryPage from './OrderHistoryPage';
import AccountPage from './AccountPage';
import BottomNav from './BottomNav'; 
import WelcomeForm from './services/Welcomeform';
import ClientSchedulePage from './ClientSchedulePage';

// === 元件與服務 ===
import { LogIn, LogOut, LayoutDashboard } from 'lucide-react';
import { getCurrentUser } from './services/supabase';
import { getUserProfile, signOut } from './services/authService';
import myLogoImg from './assets/logo-peaches.png';

// === 國際化 ===
import LanguageWrapper from './components/LanguageWrapper';
import LanguageSwitcher from './components/LanguageSwitcher';

// === 訂餐流程子頁面 ===
import DateSelection from './components/clients/DateSelection';
import MealSelection from './components/clients/MealSelection';
import PaymentPage from './components/clients/PaymentPage';
import SuccessPage from './components/clients/SuccessPage';
import TimeSelection from './components/clients/TimeSelection';
import SingleConfirmPage from './components/clients/SingleConfirmPage';

// === 後台相關元件 ===
import AdminRoute from './components/admin/AdminRoute';
import AdminLayout from './components/admin/AdminLayout'; 
import AdminDashboard from './components/admin/AdminDashboard';
import AdminOrders from './components/admin/AdminOrders';
import AdminUsers from './components/admin/AdminUsers';
import AdminMenu from './components/admin/AdminMenu';
import AdminDeliveryRoute from './components/admin/AdminDeliveryRoute';
import RoutePlanningPage from './components/admin/RoutePlanningPage';
import AdminInstantOrders from './components/admin/AdminInstantOrders';
import AdminWeeklyOrders from './components/admin/AdminWeeklyOrders';
import AdminOrderDetail from './components/admin/AdminOrderDetail';
import AdminSingleDeliveryZone from './components/admin/AdminSingleDeliveryZone';
import AdminPrinterSetup from './components/admin/AdminPrinterSetup';
import AdminSettings from './components/admin/AdminSettings';

const AdminComingSoon = ({ title }) => (
  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'calc(100vh - 200px)' }}>
    <div style={{ textAlign:'center', maxWidth:400 }}>
      <div style={{ width:64, height:64, borderRadius:'50%', background:'#f0faf5', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px', color:'#2F7D5E' }}>
        <LayoutDashboard size={28} />
      </div>
      <h2 style={{ fontSize:'1.4rem', fontWeight:600, color:'#111', marginBottom:8 }}>{title}</h2>
      <p style={{ fontSize:'1rem', color:'#666' }}>此功能暫時未開放</p>
    </div>
  </div>
);

// 1. 全域樣式 (GlobalStyles) - Peaches Cafe 風格
const GlobalStyles = ({ isAdminRoute }) => (
  <style>{`
    * { margin: 0; padding: 0; box-sizing: border-box; }
    
    body {
      font-family: 'Plus Jakarta Sans', 'Noto Sans TC', -apple-system, BlinkMacSystemFont,
                   'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
      background: ${isAdminRoute ? '#f4f7f6' : '#ffffff'};
      min-height: 100vh;
      color: #1a1a1a;
      padding-top: ${isAdminRoute ? '0' : '72px'};
      padding-bottom: ${isAdminRoute ? '0' : '72px'};
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    
    ${isAdminRoute ? `
      .admin-container {
        min-height: 100vh !important;
      }
      body {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
      }
    ` : ''}

    /* 平滑滾動 */
    html {
      scroll-behavior: smooth;
    }

    @media (prefers-reduced-motion: reduce) {
      html {
        scroll-behavior: auto;
      }
    }

    /* 手機版調整 */
    @media (max-width: 768px) {
      body {
        padding-top: ${isAdminRoute ? '0' : '64px'};
        padding-bottom: ${isAdminRoute ? '0' : '64px'};
      }
    }

    @media (max-width: 360px) {
      body {
        padding-top: ${isAdminRoute ? '0' : '60px'};
        padding-bottom: ${isAdminRoute ? '0' : '60px'};
      }
    }

    @media (max-height: 600px) and (orientation: landscape) {
      body {
        padding-top: ${isAdminRoute ? '0' : '56px'};
        padding-bottom: ${isAdminRoute ? '0' : '56px'};
      }
    }
  `}</style>
);

// 2. Header樣式 - Clean Minimal
const HeaderStyles = () => (
  <style>{`
    .app-header {
      background: #fff;
      padding: 0 clamp(16px, 4vw, 40px);
      height: 64px;
      border-bottom: 1px solid #e5e5e5;
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      z-index: 500;
      box-sizing: border-box;
    }

    .app-header.scrolled {
      box-shadow: 0 1px 3px rgba(0,0,0,0.08);
    }

    .header-left,
    .header-right {
      flex: 1;
      display: flex;
      align-items: center;
    }

    .header-right {
      justify-content: flex-end;
      gap: 0.5rem;
    }

    .header-center {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .header-logo-img {
      height: clamp(44px, 8vw, 56px);
      width: auto;
      object-fit: contain;
      cursor: pointer;
    }

    /* Base Button */
    .header-btn {
      font-family: 'Plus Jakarta Sans', 'Noto Sans TC', sans-serif;
      background: transparent;
      color: #111;
      padding: 0.5rem 1rem;
      border-radius: 999px;
      font-size: 0.875rem;
      font-weight: 400;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease;
      display: flex;
      align-items: center;
      gap: 0.375rem;
      border: 1px solid transparent;
      white-space: nowrap;
    }

    .header-btn:hover {
      background: #fafafa;
    }

    .header-btn svg {
      width: 18px;
      height: 18px;
      flex-shrink: 0;
    }

    /* Language Switcher */
    .header-btn-lang {
      color: #666;
      gap: 0.25rem;
      padding: 0.5rem 0.75rem;
    }

    .header-btn-lang:hover {
      color: #111;
      background: #fafafa;
    }

    /* Admin Button */
    .header-btn-admin {
      color: #2F7D5E;
      border: 1px solid #e5e5e5;
    }

    .header-btn-admin:hover {
      background: #f0f7f4;
      border-color: #2F7D5E;
    }

    /* Logout Button */
    .header-btn-logout {
      color: #999;
    }

    .header-btn-logout:hover {
      color: #d32f2f;
      background: #fef2f2;
    }

    /* Login Button - Primary */
    .header-btn-login {
      background: #2F7D5E;
      color: #fff;
      border: 1px solid #2F7D5E;
    }

    .header-btn-login:hover {
      background: #245F48;
      border-color: #245F48;
    }

    /* RWD */
    @media (max-width: 768px) {
      .app-header {
        padding: 0 16px;
        height: 60px;
      }

      .header-logo-img {
        height: 42px;
      }

      .header-btn {
        padding: 0.5rem;
      }

      .btn-text {
        display: none;
      }
    }

    @media (max-width: 360px) {
      .app-header {
        padding: 0 12px;
        height: 56px;
      }

      .header-logo-img {
        height: 38px;
      }

      .header-right {
        gap: 0.25rem;
      }
    }

    @media (max-height: 600px) and (orientation: landscape) {
      .app-header {
        height: 52px;
        padding: 0 16px;
      }

      .header-logo-img {
        height: 38px;
      }
    }

    /* Accessibility */
    .header-btn:focus-visible {
      outline: 2px solid #2F7D5E;
      outline-offset: 2px;
    }

    @media (prefers-reduced-motion: reduce) {
      .app-header, .header-btn {
        transition: none !important;
      }
    }
  `}</style>
);

function AppContent() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showWelcomeForm, setShowWelcomeForm] = useState(false);
  const [pendingRoute, setPendingRoute] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // 監聽滾動
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    if (!isAdminRoute) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [isAdminRoute]);

  useEffect(() => {
    checkUserStatus();
  }, []);

  const checkUserStatus = async () => {
    try {
      const user = await getCurrentUser();
      if (user) {
        setIsLoggedIn(true);
        setCurrentUser(user);
        const profileResult = await getUserProfile(user.id);
        if (profileResult.success && profileResult.data) {
          setUserProfile(profileResult.data);
          if (!profileResult.data.name) setShowWelcomeForm(true);
        }
      }
    } catch (error) {
      // silently ignore
    }
  };

  const handleLogin = async (phoneNumber, user) => {
    setIsLoggedIn(true);
    setCurrentUser(user);
    setShowLoginModal(false);
    const profileResult = await getUserProfile(user.id);
    if (profileResult.success && profileResult.data) {
      setUserProfile(profileResult.data);
      if (!profileResult.data.name) setShowWelcomeForm(true);
    }
    if (pendingRoute) {
      navigate(pendingRoute);
      setPendingRoute(null);
    }
  };

  const handleWelcomeComplete = (updatedProfile) => {
    setUserProfile(updatedProfile);
    setShowWelcomeForm(false);
  };

  const handleProfileUpdate = (updatedProfile) => {
    setUserProfile(updatedProfile);
  };

  const handleLogout = async () => {
    if (window.confirm('確定要登出嗎？')) {
      try {
        const result = await signOut();
        if (result.success) {
          setIsLoggedIn(false);
          setCurrentUser(null);
          setUserProfile(null);
          setPendingRoute(null);
          navigate('/');
        }
      } catch (error) {
        // Force logout on client side even if server call fails
        setIsLoggedIn(false);
        setCurrentUser(null);
        setUserProfile(null);
        navigate('/');
      }
    }
  };
  
  const requestLogin = () => {
    setShowLoginModal(true);
  };

  const handleCloseLogin = () => {
    setShowLoginModal(false);
    setPendingRoute(null);
  };

  const goHome = () => {
    const langMatch = location.pathname.match(/^\/(en)(\/|$)/);
    const langPrefix = langMatch ? `/${langMatch[1]}` : '';
    navigate(`${langPrefix}/`);
  };
  
  return (
    <>
      <GlobalStyles isAdminRoute={isAdminRoute} />
      <HeaderStyles />
      
      {!isAdminRoute && (
        <header className={`app-header ${scrolled ? 'scrolled' : ''}`}>
          <div className="header-left"></div>
          
          <div className="header-center">
            <img 
              src={myLogoImg} 
              alt="Peaches Cafe Logo" 
              className="header-logo-img"
              onClick={goHome}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  goHome();
                }
              }}
            />
          </div>
          
          <div className="header-right">
            {/* 語言切換 */}
            <LanguageSwitcher />

            {/* Admin 按鈕 */}
            {isLoggedIn && userProfile?.role === 'admin' && (
              <button 
                onClick={() => navigate('/admin')} 
                className="header-btn header-btn-admin"
                title="管理後台"
                aria-label="前往管理後台"
              >
                <LayoutDashboard size={20} /> 
                <span className="btn-text">後台</span>
              </button>
            )}

            {/* 登入/登出 按鈕 */}
            {isLoggedIn ? (
              <button 
                onClick={handleLogout} 
                className="header-btn header-btn-logout"
                title="登出"
                aria-label="登出帳號"
              >
                <LogOut size={20} /> 
                <span className="btn-text">登出</span>
              </button>
            ) : (
              <button 
                onClick={requestLogin} 
                className="header-btn header-btn-login"
                title="登入 / 註冊"
                aria-label="登入或註冊"
              >
                <LogIn size={20} /> 
                <span className="btn-text">登入</span>
              </button>
            )}
          </div>
        </header>
      )}
      
      <main>
        <Routes>
          {/* ✅ 後台路由區域（不需要 i18n） */}
          <Route
            path="/admin"
            element={
              <AdminRoute user={userProfile || currentUser}>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="instant-orders" element={<AdminInstantOrders />} />
            <Route path="weekly-orders" element={<AdminComingSoon title="週餐訂閱" />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="orders/:orderId" element={<AdminOrderDetail />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="delivery-schedule" element={<AdminComingSoon title="配送排程" />} />
            <Route path="route" element={<AdminComingSoon title="路線規劃" />} />
            <Route path="single-delivery-zone" element={<AdminComingSoon title="單點配送區域" />} />
            <Route path="menu" element={<AdminMenu />} />
            <Route path="printer-setup" element={<AdminPrinterSetup />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          {/* 前台路由 - 支援語言前綴 (/ = 中文, /en = 英文) */}
          {['/', '/:lang'].map((prefix) => (
            <Route key={prefix} path={prefix} element={<LanguageWrapper />}>
              <Route index element={<HomePage />} />

              <Route
                path="booking/:bookingMode"
                element={
                  <RestaurantBooking
                    isLoggedIn={isLoggedIn}
                    requestLogin={requestLogin}
                    user={userProfile || currentUser}
                  />
                }
              >
                <Route index element={<DateSelection />} />
                <Route path="meals" element={<MealSelection />} />
                <Route path="time" element={<TimeSelection />} />
                <Route path="confirm" element={<SingleConfirmPage />} />
                <Route path="payment" element={<PaymentPage />} />
                <Route path="success" element={<SuccessPage />} />
              </Route>

              <Route
                path="schedule"
                element={
                  isLoggedIn && currentUser ? (
                    <ClientSchedulePage currentUser={currentUser} />
                  ) : (
                    <div style={{
                      padding: '80px 20px',
                      textAlign: 'center',
                      maxWidth: '500px',
                      margin: '0 auto'
                    }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        margin: '0 auto 24px',
                        background: 'linear-gradient(135deg, #2F7D5E, #48A67C)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '2.5rem'
                      }}>
                        🔐
                      </div>
                      <h3 style={{
                        fontSize: '1.8rem',
                        color: '#2F7D5E',
                        marginBottom: '12px',
                        fontWeight: '400'
                      }}>
                        請先登入
                      </h3>
                      <p style={{
                        marginBottom: '28px',
                        color: '#6B7280',
                        fontSize: '1.05rem',
                        lineHeight: '1.6'
                      }}>
                        登入後即可查看您的專屬配送日程
                      </p>
                      <button
                        onClick={requestLogin}
                        style={{
                          padding: '14px 32px',
                          background: 'linear-gradient(135deg, #2F7D5E, #48A67C)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '1.05rem',
                          cursor: 'pointer',
                          fontWeight: '400',
                          boxShadow: '0 4px 12px rgba(47, 125, 94, 0.3)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 6px 16px rgba(47, 125, 94, 0.4)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 12px rgba(47, 125, 94, 0.3)';
                        }}
                      >
                        立即登入
                      </button>
                    </div>
                  )
                }
              />

              <Route path="orders" element={<OrderHistoryPage />} />

              <Route
                path="account"
                element={
                  <AccountPage
                    currentUser={currentUser}
                    userProfile={userProfile}
                    onProfileUpdate={handleProfileUpdate}
                  />
                }
              />
            </Route>
          ))}
        </Routes>
      </main>
      
      {showLoginModal && (
        <LoginPage 
          onLogin={handleLogin}
          onClose={handleCloseLogin}
        />
      )}

      {showWelcomeForm && currentUser && (
        <WelcomeForm 
          userId={currentUser.id}
          phoneNumber={currentUser.user_metadata?.phone || currentUser.email?.split('@')[0] || ''}
          onComplete={handleWelcomeComplete}
        />
      )}

      {/* 使用新的 Peaches BottomNav */}
      {!isAdminRoute && (
        <BottomNav 
          isLoggedIn={isLoggedIn}
          requestLogin={requestLogin}
          setPendingRoute={setPendingRoute}
          onGoHome={goHome}
        />
      )}
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;